import { LIMITS, type ConversationSummary, type ServerInfo } from '@sd/contracts';
import { useRef, useState } from 'react';
import { Icon } from '../icons/Icon';
import { AvatarStack } from '../ui/Avatar';
import { IconButton } from '../ui/controls';
import { ConversationAvatar, usePerson } from './bits';
import { Composer } from './Composer';
import { useAppEnv, useClient, useClientState } from './context';
import { conversationName, isSelfChat, uploadLimitsText } from './copy';
import { useShell } from './shell';
import { Timeline } from './Timeline';

function ChatHead({ conversation }: { conversation: ConversationSummary }) {
  const env = useAppEnv();
  const shell = useShell();
  const details = useClientState((s) => s.details[conversation.id]);
  const meId = useClientState((s) => s.me?.id);
  const peer = usePerson(conversation.peer?.id);
  const today = env.now().toDateString();
  let sub: string;
  if (conversation.kind === 'group') {
    const joinedToday = details?.members.find((m) => m.person.id !== meId && new Date(m.joinedAt).toDateString() === today && new Date(m.joinedAt).getTime() > new Date(conversation.createdAt).getTime() + 60_000);
    sub = conversation.my.state !== 'active' ? '나간 대화 · 읽기 전용' : `${conversation.memberCount}명${joinedToday ? ` · 오늘 ${joinedToday.person.displayName}님 참여` : ''}`;
  } else if (isSelfChat(conversation)) {
    sub = '나만 보는 대화 · 메모와 파일 보관';
  } else {
    const p = peer ?? conversation.peer;
    sub = conversation.readOnlyReason === 'peer_deactivated' ? '비활성화된 계정' : `${p?.department ?? ''} · ${p?.online ? '온라인' : '오프라인'}`;
  }
  const stackPeople = (details?.members.map((m) => m.person) ?? conversation.memberPreview).slice(0, 4);
  return (
    <header className="chat-head">
      <IconButton icon="back" className="ch-back" label="대화 목록으로" onClick={() => shell.setView('list')} />
      <div className="ch-title">
        <ConversationAvatar conversation={conversation} />
        <div style={{ minWidth: 0 }}>
          <div className="ch-name">
            <span className="ell">{conversationName(conversation)}</span>
            {conversation.my.muted ? <Icon name="belloff" size="xs" /> : null}
          </div>
          <div className="ch-sub ell">{sub}</div>
        </div>
      </div>
      <div className="ch-acts">
        {conversation.kind === 'group' ? <AvatarStack people={stackPeople} total={conversation.memberCount} /> : null}
        <IconButton icon="search" className="hide-xs" label="대화 내 검색" tip="대화 내 검색" tipPos="below" onClick={() => env.openModal({ kind: 'search', scope: conversation.id })} />
        <IconButton icon="info" id="info-btn" on={shell.detailOpen && !shell.single} label="대화 정보" tip="대화 정보" tipPos="left" onClick={() => shell.toggleDetail()} />
      </div>
    </header>
  );
}

/**
 * What owners and admins are told about the virus scanner while they chat (it used to show only on the admin
 * screen, so a stopped clamd went unnoticed): null while it works, and in a development build's mock.
 */
export function scannerProblem(scanner: ServerInfo['scanner'] | undefined): string | null {
  if (!scanner || scanner.mode === 'development-mock' || scanner.status === 'ok') return null;
  return scanner.status === 'degraded'
    ? '악성코드 정의 업데이트가 멈췄어요 · 검사는 되지만 새 악성코드를 놓칠 수 있어요'
    : '첨부 파일 검사기가 멈췄어요 · 새로 올린 파일은 검사가 끝날 때까지 보낼 수 없어요';
}

/** The scanner problem an admin closed, per client: it shows again once the problem changes. */
const closedProblems = new WeakMap<object, string>();

function Banner({ conversationId }: { conversationId: string }) {
  const env = useAppEnv();
  const client = useClient();
  const connection = useClientState((s) => s.connection);
  const admin = useClientState((s) => s.me?.role === 'owner' || s.me?.role === 'admin');
  const problem = scannerProblem(useClientState((s) => s.serverInfo?.scanner));
  const [, setClosed] = useState(0);
  const maintenance = useClientState((s) => s.serverInfo?.state === 'MAINTENANCE');
  const pending = useClientState((s) => s.pending);
  const queued = Object.values(pending).filter((p) => p.conversationId === conversationId && p.state === 'queued').length;
  if (connection === 'offline')
    return (
      <div className="banner show offline" role="status" aria-live="polite">
        <Icon name="wifioff" />
        <span>연결이 끊겼어요 · 재연결 중…{queued ? ` 대기 중인 메시지 ${queued}개는 연결되면 전송돼요` : ''}</span>
      </div>
    );
  if (maintenance)
    return (
      <div className="banner show offline" role="status" aria-live="polite">
        <Icon name="server" />
        <span>서버 점검 중이에요 · 메시지는 점검이 끝나면 보내져요</span>
      </div>
    );
  if (connection === 'syncing')
    return (
      <div className="banner show syncing" role="status" aria-live="polite">
        <Icon name="sync" className="spin" />
        <span>다시 연결됨 · 놓친 메시지를 동기화하는 중…</span>
        <span className="bar">
          <i />
        </span>
      </div>
    );
  // Everything else is above: the scanner waits while the connection is the problem.
  if (admin && problem && closedProblems.get(client) !== problem)
    return (
      <div className="banner show offline" role="status" aria-live="polite">
        <Icon name="alert" />
        <span>{problem}</span>
        <button type="button" className="linkbtn" style={{ marginLeft: 'auto', flex: 'none' }} onClick={() => env.navigate('admin')}>
          회사 설정 보기
        </button>
        <IconButton
          icon="x"
          iconSize="sm"
          label="검사기 알림 닫기"
          onClick={() => {
            closedProblems.set(client, problem);
            setClosed((n) => n + 1);
          }}
        />
      </div>
    );
  return <div className="banner" role="status" aria-live="polite" />;
}

/** Centre pane: conversation header, timeline and composer (design `main.chat`). */
export function ChatPane() {
  const client = useClient();
  const conversation = useClientState((s) => (s.activeConversationId ? s.conversations[s.activeConversationId] : undefined));
  const limits = useClientState((s) => s.serverInfo?.limits ?? LIMITS);
  const [dropping, setDropping] = useState(false);
  const depth = useRef(0);
  const hasFiles = (e: React.DragEvent) => [...(e.dataTransfer?.types ?? [])].includes('Files');

  if (!conversation) {
    return (
      <main className="chat pane glass" id="chat" aria-label="대화">
        <div className="chat-top" style={{ display: 'grid', placeItems: 'center' }}>
          <div className="empty-note">왼쪽에서 대화를 선택하거나 새 대화를 시작하세요</div>
        </div>
      </main>
    );
  }

  return (
    <main
      className="chat pane glass"
      id="chat"
      aria-label="대화"
      onDragEnter={(e) => {
        if (!hasFiles(e) || conversation.readOnly) return;
        e.preventDefault();
        depth.current += 1;
        setDropping(true);
      }}
      onDragOver={(e) => {
        if (hasFiles(e)) e.preventDefault();
      }}
      onDragLeave={() => {
        depth.current = Math.max(0, depth.current - 1);
        if (!depth.current) setDropping(false);
      }}
      onDrop={(e) => {
        e.preventDefault();
        depth.current = 0;
        setDropping(false);
        if (conversation.readOnly) return;
        const files = [...(e.dataTransfer?.files ?? [])];
        if (files.length) client.addFiles(conversation.id, files);
      }}
    >
      <div className="chat-top">
        <ChatHead conversation={conversation} />
        <Banner conversationId={conversation.id} />
        <Timeline key={conversation.id} conversation={conversation} />
      </div>
      <Composer conversation={conversation} />
      <div className={`drop${dropping ? ' show' : ''}`}>
        <div>
          <Icon name="upload" size="lg" style={{ width: 40, height: 40 }} />
          <div>여기에 놓으면 첨부돼요</div>
          <small>{uploadLimitsText(limits)}</small>
        </div>
      </div>
    </main>
  );
}
